import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import { jobCardsApi, quotationsApi } from "@/services/api";
import { JobCardForm, EMPTY_FORM } from "@/pages/jobcards/types";
import SectionCompanyProfile from "@/pages/jobcards/components/SectionCompanyProfile";
import SectionJobInfo from "@/pages/jobcards/components/SectionJobInfo";
import SectionClientDetails from "@/pages/jobcards/components/SectionClientDetails";
import SectionProjectServices from "@/pages/jobcards/components/SectionProjectServices";
import SectionTeamScope from "@/pages/jobcards/components/SectionTeamScope";
import SectionDeliveryStatus from "@/pages/jobcards/components/SectionDeliveryStatus";
import JobCardSidebar from "@/pages/jobcards/components/JobCardSidebar";

interface QuotationOption {
  id: number;
  quotation_no: string;
  title: string;
  client_name: string;
  total: number;
  status: string;
}

export default function NewJobCardPage() {
  const navigate = useNavigate();
  const { settings } = useCompanySettings();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [jobNoLoading, setJobNoLoading] = useState(true);
  const fetchedRef = useRef(false);
  const lastPrefixRef = useRef<string>('');
  const [quotations, setQuotations] = useState<QuotationOption[]>([]);
  const [quotationsLoading, setQuotationsLoading] = useState(false);
  const [quotationSearch, setQuotationSearch] = useState("");
  const [showQuotationDropdown, setShowQuotationDropdown] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState<JobCardForm>(() => ({
    ...EMPTY_FORM,
    companyName: settings.name,
    companyAddress: settings.address,
    companyEmail: settings.email,
    companyPhone: settings.phone,
    companyGstin: settings.gstin,
    companyLogo: settings.logo,
    jobNo: "Generating...",
    createdDate: today,
    assignedDate: today,
    statusTimeline: [
      {
        status: "Pending",
        date: today,
        time: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
        user: "System",
        description: "Job Card created and set to Pending",
      },
    ],
  }));

  const fetchJobNumber = useCallback(async (silent = false) => {
    if (!silent) setJobNoLoading(true);
    try {
      const data = await jobCardsApi.getNextNumber() as { job_no: string };
      setForm((prev) => ({ ...prev, jobNo: data.job_no }));
    } catch {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const rand = String(Math.floor(Math.random() * 9000) + 1000);
      const fallback = `${settings.branchPrefix.toUpperCase()}-${settings.jobCardPrefix.toUpperCase()}-${yyyy}-${mm}-${rand}`;
      setForm((prev) => ({ ...prev, jobNo: fallback }));
    } finally {
      setJobNoLoading(false);
    }
  }, [settings.branchPrefix, settings.jobCardPrefix]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    lastPrefixRef.current = `${settings.branchPrefix}|${settings.jobCardPrefix}`;
    fetchJobNumber(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handlePrefixChange = (e: Event) => {
      const detail = (e as CustomEvent).detail as Record<string, string> | undefined;
      if (!detail) return;
      const newBranch = detail.branchPrefix ?? detail.branch_prefix ?? '';
      const newJobPrefix = detail.jobCardPrefix ?? detail.job_card_prefix ?? '';
      const newKey = `${newBranch}|${newJobPrefix}`;
      if (newKey !== lastPrefixRef.current && (newBranch || newJobPrefix)) {
        lastPrefixRef.current = newKey;
        setForm((prev) => ({ ...prev, jobNo: 'Refreshing...' }));
        setJobNoLoading(true);
        jobCardsApi.getNextNumber()
          .then((data: unknown) => {
            const d = data as { job_no: string };
            setForm((prev) => ({ ...prev, jobNo: d.job_no }));
          })
          .catch(() => {
            const now = new Date();
            const yyyy = now.getFullYear();
            const mm = String(now.getMonth() + 1).padStart(2, "0");
            const rand = String(Math.floor(Math.random() * 9000) + 1000);
            const branch = newBranch || settings.branchPrefix;
            const prefix = newJobPrefix || settings.jobCardPrefix;
            setForm((prev) => ({ ...prev, jobNo: `${branch.toUpperCase()}-${prefix.toUpperCase()}-${yyyy}-${mm}-${rand}` }));
          })
          .finally(() => setJobNoLoading(false));
      }
    };
    window.addEventListener('company-settings-updated', handlePrefixChange);
    return () => window.removeEventListener('company-settings-updated', handlePrefixChange);
  }, [settings.branchPrefix, settings.jobCardPrefix]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      companyName: settings.name,
      companyAddress: settings.address,
      companyEmail: settings.email,
      companyPhone: settings.phone,
      companyGstin: settings.gstin,
      companyLogo: settings.logo,
    }));
  }, [settings]);

  const onChange = (patch: Partial<JobCardForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  // Fetch approved/accepted quotations for linking
  const fetchQuotations = useCallback(async (search?: string) => {
    setQuotationsLoading(true);
    try {
      const res = await quotationsApi.getAll({ status: "approved", search, limit: 50 }) as { data?: QuotationOption[] } | QuotationOption[];
      const raw = Array.isArray(res) ? res : (res as { data?: QuotationOption[] }).data || [];
      setQuotations(raw as QuotationOption[]);
    } catch {
      setQuotations([]);
    } finally {
      setQuotationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (form.quotationType === "with_quotation") {
      fetchQuotations();
    }
  }, [form.quotationType, fetchQuotations]);

  // ── Build the payload the backend expects ─────────────────────────────────
  // Backend POST /api/job-cards expects:
  //   title (required), client_id (int, required), location, priority,
  //   due_date, area, remarks, services (array), field_team (array of emp ids),
  //   office_team (array of emp ids)
  const buildPayload = (isDraft = false) => {
    if (!form.projectTitle?.trim()) {
      throw new Error("Project title is required");
    }
    if (!form.clientId) {
      throw new Error("Please select a client");
    }

    // Extract employee IDs from "id::name" format stored in fieldTeam/officeTeam
    const fieldTeamIds = form.fieldTeam
      .map((k) => Number(k.split("::")[0]))
      .filter((id) => id > 0);
    const officeTeamIds = form.officeTeam
      .map((k) => Number(k.split("::")[0]))
      .filter((id) => id > 0);

    return {
      title:       form.projectTitle.trim(),
      client_id:   Number(form.clientId),
      location:    form.surveyLocation || "",
      priority:    (form.priority || "medium").toLowerCase(),
      due_date:    form.completionDate || null,
      area:        form.area || "",
      remarks:     form.scopeOfWork || "",
      assigned_date: form.assignedDate || today,
      status:      isDraft ? "pending" : "pending",
      // Services as array of objects
      services:    form.services.map((s) => ({ name: s, qty: 1, unit: "lump sum", rate: 0 })),
      // Team members — extract IDs from form
      field_team:  fieldTeamIds,
      office_team: officeTeamIds,
      quotation_type: form.quotationType || null,
      linked_quotation_id: form.linkedQuotationId ? Number(form.linkedQuotationId) : null,
      linked_quotation_no: form.linkedQuotationNo || null,
      field_visit_required: form.fieldVisitRequired,
    };
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const payload = buildPayload(true);
      await jobCardsApi.create(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to save draft";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const payload = buildPayload(false);
      await jobCardsApi.create(payload);
      setSaved(true);
      setTimeout(() => {
        navigate("/job-cards");
      }, 1200);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to submit job card";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ERPLayout title="New Job Card">
      <div className="p-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/job-cards")}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 cursor-pointer transition-all"
            >
              <i className="ri-arrow-left-line text-sm" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Create New Job Card</h2>
              <p className="text-sm text-gray-500 mt-0.5">Fill all sections to generate a complete job card</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg font-mono font-semibold flex items-center gap-1.5">
              {jobNoLoading && <i className="ri-loader-4-line animate-spin text-xs" />}
              {form.jobNo}
            </span>
            <span className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700">
              {form.currentStatus}
            </span>
          </div>
        </div>

        {/* Inline error banner */}
        {saveError && (
          <div className="mb-4 flex items-center gap-3 p-3.5 bg-red-50 border border-red-200 rounded-xl">
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-fill text-red-500 text-lg" />
            </span>
            <p className="text-sm text-red-700 flex-1">{saveError}</p>
            <button
              onClick={() => setSaveError("")}
              className="w-5 h-5 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        )}

        {/* Success banner */}
        {saved && (
          <div className="mb-4 flex items-center gap-3 p-3.5 bg-amber-50 border border-amber-200 rounded-xl">
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-circle-fill text-amber-500 text-lg" />
            </span>
            <p className="text-sm text-amber-700 font-medium">Job card saved successfully!</p>
          </div>
        )}

        {/* Main Layout */}
        <div className="flex flex-col xl:flex-row gap-6 items-start">
          {/* Left — Form Sections */}
          <div className="flex-1 min-w-0 space-y-4">
            <SectionCompanyProfile form={form} />

            {/* Quotation Type Selector */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-amber-600 text-base" />
                </span>
                <h3 className="text-sm font-semibold text-gray-800">Section 1B — Job Contract Type</h3>
              </div>
              <div className="p-5">
                <p className="text-xs text-gray-500 mb-4">Select how this job was contracted — with or without a prior quotation</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  {([
                    {
                      value: "with_quotation" as const,
                      icon: "ri-file-text-line",
                      title: "With Quotation",
                      desc: "A quotation was already generated and approved by the client",
                    },
                    {
                      value: "without_quotation" as const,
                      icon: "ri-shake-hands-line",
                      title: "Without Quotation",
                      desc: "Direct job contract — client confirmed without a formal quotation",
                    },
                  ] as const).map((opt) => {
                    const isActive = form.quotationType === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange({ quotationType: opt.value, linkedQuotationId: "", linkedQuotationNo: "" })}
                        className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
                          isActive
                            ? "border-amber-400 bg-amber-50"
                            : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"
                        }`}
                      >
                        <span className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${
                          isActive ? "bg-amber-100" : "bg-gray-100"
                        }`}>
                          <i className={`${opt.icon} text-lg ${isActive ? "text-amber-600" : "text-gray-400"}`} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${isActive ? "text-amber-800" : "text-gray-700"}`}>{opt.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{opt.desc}</p>
                        </div>
                        {isActive && (
                          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-500 flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-white text-xs" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Quotation search — only shown when "with_quotation" is selected */}
                {form.quotationType === "with_quotation" && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Link Approved Quotation <span className="text-red-400">*</span>
                    </label>
                    {form.linkedQuotationId ? (
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                        <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0">
                          <i className="ri-file-text-line text-amber-600" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-amber-800">{form.linkedQuotationNo}</p>
                          <p className="text-xs text-amber-600">Quotation linked successfully</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => onChange({ linkedQuotationId: "", linkedQuotationNo: "" })}
                          className="w-6 h-6 flex items-center justify-center text-amber-400 hover:text-red-500 cursor-pointer"
                        >
                          <i className="ri-close-line text-sm" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none z-10">
                          {quotationsLoading
                            ? <i className="ri-loader-4-line animate-spin text-gray-400 text-sm" />
                            : <i className="ri-search-line text-gray-400 text-sm" />
                          }
                        </span>
                        <input
                          type="text"
                          value={quotationSearch}
                          onChange={(e) => {
                            setQuotationSearch(e.target.value);
                            fetchQuotations(e.target.value);
                          }}
                          onFocus={() => setShowQuotationDropdown(true)}
                          onBlur={() => setTimeout(() => setShowQuotationDropdown(false), 200)}
                          placeholder="Search by quotation number or client name..."
                          className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
                        />
                        {showQuotationDropdown && (
                          <div
                            className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl z-20 overflow-hidden max-h-56 overflow-y-auto"
                            style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
                          >
                            {quotationsLoading && (
                              <div className="px-4 py-3 text-sm text-gray-400 flex items-center gap-2">
                                <i className="ri-loader-4-line animate-spin" />Loading quotations...
                              </div>
                            )}
                            {!quotationsLoading && quotations.length === 0 && (
                              <div className="px-4 py-3 text-sm text-gray-400">
                                No approved quotations found
                                <a href="/billing/quotations" className="ml-2 text-amber-600 hover:underline text-xs">Create one</a>
                              </div>
                            )}
                            {!quotationsLoading && quotations.map((q) => (
                              <button
                                key={q.id}
                                type="button"
                                onMouseDown={() => {
                                  onChange({ linkedQuotationId: String(q.id), linkedQuotationNo: q.quotation_no });
                                  setQuotationSearch(q.quotation_no);
                                  setShowQuotationDropdown(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-amber-50 cursor-pointer text-left transition-colors border-b border-gray-50 last:border-0"
                              >
                                <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                                  <i className="ri-file-text-line text-amber-600 text-sm" />
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-800">{q.quotation_no}</p>
                                  <p className="text-xs text-gray-500 truncate">{q.client_name} — {q.title}</p>
                                </div>
                                <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                                  ₹{(q.total || 0).toLocaleString("en-IN")}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                      <i className="ri-information-line" />
                      Only approved quotations are shown. Go to Billing &gt; Quotations to approve one.
                    </p>
                  </div>
                )}

                {form.quotationType === "without_quotation" && (
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-information-line text-gray-400 text-sm" />
                    </span>
                    <p className="text-xs text-gray-500">No quotation required. This job was directly contracted by the client.</p>
                  </div>
                )}
              </div>
            </div>

            <SectionJobInfo form={form} onChange={onChange} />
            <SectionClientDetails form={form} onChange={onChange} />
            <SectionProjectServices form={form} onChange={onChange} />
            <SectionTeamScope form={form} onChange={onChange} />
            <SectionDeliveryStatus form={form} onChange={onChange} />

            {/* Bottom action bar */}
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center justify-between gap-3 flex-wrap">
              <button
                onClick={() => navigate("/job-cards")}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-all"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-close-line text-sm" /></span>
                Cancel
              </button>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-all disabled:opacity-50"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line text-sm" /></span>
                  Save Draft
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-all disabled:opacity-50"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${saving ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} text-sm`} />
                  </span>
                  {saving ? "Submitting..." : "Submit Job Card"}
                </button>
              </div>
            </div>
          </div>

          {/* Right — Sidebar */}
          <div className="w-full xl:w-72 flex-shrink-0 xl:sticky xl:top-4">
            <JobCardSidebar
              form={form}
              onSaveDraft={handleSaveDraft}
              onSubmit={handleSubmit}
              onPreview={() => setShowPreview(true)}
              saving={saving}
              saved={saved}
            />
          </div>
        </div>
      </div>

      {/* Print Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-semibold text-gray-900">Job Card Preview</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-2 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line text-sm" /></span>
                  Print / PDF
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>
            </div>
            <div className="p-8 print:p-0" id="job-card-print">
              <div className="border-b-2 border-amber-500 pb-4 mb-6 flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  {form.companyLogo && (
                    <img src={form.companyLogo} alt="logo" className="w-12 h-12 object-contain" />
                  )}
                  <div>
                    <h1 className="text-lg font-bold text-gray-900">{form.companyName}</h1>
                    <p className="text-xs text-gray-500">{form.companyAddress}</p>
                    <p className="text-xs text-gray-500">{form.companyEmail} | {form.companyPhone}</p>
                    {form.companyGstin && <p className="text-xs text-gray-500">GSTIN: {form.companyGstin}</p>}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Job Card</p>
                  <p className="text-xl font-bold text-amber-600 font-mono">{form.jobNo}</p>
                  <p className="text-xs text-gray-500">Created: {form.createdDate}</p>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{form.currentStatus}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Client Details</p>
                  <p className="text-sm font-semibold text-gray-900">{form.clientName || "—"}</p>
                  <p className="text-xs text-gray-600">{form.clientCompany}</p>
                  <p className="text-xs text-gray-600">{form.clientEmail}</p>
                  <p className="text-xs text-gray-600">{form.clientPhone}</p>
                  {form.clientGstin && <p className="text-xs text-gray-600">GSTIN: {form.clientGstin}</p>}
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Project Details</p>
                  <p className="text-sm font-semibold text-gray-900">{form.projectTitle || "—"}</p>
                  <p className="text-xs text-gray-600">Location: {form.surveyLocation}</p>
                  <p className="text-xs text-gray-600">Area: {form.area}</p>
                  <p className="text-xs text-gray-600">Assigned: {form.assignedDate}</p>
                  <p className="text-xs text-gray-600">Due: {form.completionDate}</p>
                </div>
              </div>

              {form.services.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Services</p>
                  <div className="flex flex-wrap gap-1.5">
                    {form.services.map((s) => (
                      <span key={s} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {form.scopeOfWork && (
                <div className="mb-4">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Scope of Work</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{form.scopeOfWork}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mb-4">
                {form.fieldTeam.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Field Team</p>
                    {form.fieldTeam.map((m) => <p key={m} className="text-xs text-gray-700">• {m}</p>)}
                  </div>
                )}
                {form.officeTeam.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Office Team</p>
                    {form.officeTeam.map((m) => <p key={m} className="text-xs text-gray-700">• {m}</p>)}
                  </div>
                )}
              </div>

              {form.deliverables.length > 0 && (
                <div className="mb-6">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Deliverables</p>
                  <div className="flex flex-wrap gap-1.5">
                    {form.deliverables.map((d) => (
                      <span key={d} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{d}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-gray-200 grid grid-cols-3 gap-6">
                {["Field Surveyor", "Survey Manager", "Client"].map((role) => (
                  <div key={role} className="text-center">
                    <div className="h-12 border-b border-gray-400 mb-2" />
                    <p className="text-xs font-semibold text-gray-600">{role}</p>
                    <p className="text-xs text-gray-400">Signature &amp; Date</p>
                  </div>
                ))}
              </div>

              <p className="text-center text-xs text-gray-400 mt-6">
                This is a computer-generated Job Card. | {form.companyName} | {form.jobNo}
              </p>
            </div>
          </div>
        </div>
      )}
    </ERPLayout>
  );
}
